import { InboxOutlined } from '@ant-design/icons';
import { cityDisplayName, countyDisplayName, type ImportPreview, type ImportPreviewRow } from '@financiar/shared';
import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Modal, Spin, Table, Tag, Tooltip, Typography, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { RcFile } from 'antd/es/upload';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import { commitClientImport, previewClientImport } from './api';

const { Paragraph } = Typography;
const { Dragger } = Upload;

type ImportPhase = 'upload' | 'preview' | 'importing';

const STATUS_TAG: Record<ImportPreviewRow['status'], { color: string; label: string }> = {
  new: { color: 'green', label: 'Nou' },
  duplicate: { color: 'default', label: 'Duplicat' },
  error: { color: 'red', label: 'Eroare' },
};

/**
 * "Importă clienți" modal (`ClientiListPage`'s toolbar). Two real phases:
 * upload a file and get a preview back (writes nothing server-side), then
 * review + commit it (`importing` is really just the commit mutation's
 * in-flight state, kept as its own phase so the preview table can be
 * disabled with a Spin overlay while the request is in the air instead of
 * silently re-enabling the buttons underneath it).
 */
export function ClientImportModal({
  open,
  firmaId,
  onClose,
  onDone,
}: {
  open: boolean;
  firmaId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { message } = App.useApp();
  const [phase, setPhase] = useState<ImportPhase>('upload');
  const [preview, setPreview] = useState<ImportPreview | null>(null);

  function resetToUpload() {
    setPhase('upload');
    setPreview(null);
  }

  function handleClose() {
    resetToUpload();
    onClose();
  }

  const previewMutation = useMutation({
    mutationFn: (file: File) => previewClientImport(firmaId, file),
    onSuccess: (data) => {
      setPreview(data);
      setPhase('preview');
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Fișierul nu a putut fi verificat.');
    },
  });

  const newRows = preview?.rows.filter((row) => row.status === 'new' && row.client) ?? [];

  const commitMutation = useMutation({
    mutationFn: () => {
      const clients = newRows.map((row) => row.client).filter((client) => client != null);
      return commitClientImport(firmaId, clients);
    },
    onMutate: () => setPhase('importing'),
    onSuccess: (result) => {
      const parts = [`${result.created} client${result.created === 1 ? '' : 'i'} importat${result.created === 1 ? '' : 'i'}`];
      if (result.skipped > 0) parts.push(`${result.skipped} sărit${result.skipped === 1 ? '' : 'i'} (deja existent${result.skipped === 1 ? '' : 'i'})`);
      if (result.failed > 0) parts.push(`${result.failed} eșuat${result.failed === 1 ? '' : 'i'}`);
      message.success(`${parts.join(', ')}.`);
      onDone();
      handleClose();
    },
    onError: (error) => {
      setPhase('preview');
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Importul a eșuat.');
    },
  });

  const columns: ColumnsType<ImportPreviewRow> = [
    { title: 'Rând', dataIndex: 'rowNumber', width: 64 },
    { title: 'Denumire', dataIndex: 'name', ellipsis: true },
    { title: 'CUI', dataIndex: 'cui', width: 110, render: (value: string | null) => value ?? '—' },
    {
      title: 'Județ / Localitate',
      key: 'locatie',
      render: (_, row) =>
        row.countyCode && row.cityName ? `${countyDisplayName(row.countyCode)} / ${cityDisplayName(row.countyCode, row.cityName)}` : '—',
    },
    {
      title: 'TVA',
      dataIndex: 'isVatPayer',
      width: 110,
      render: (isVatPayer: boolean, row) =>
        row.status === 'error' ? '—' : <Tag color={isVatPayer ? 'blue' : 'default'}>{isVatPayer ? 'Plătitor' : 'Neplătitor'}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, row) => {
        const { color, label } = STATUS_TAG[row.status];
        const notes = [...row.errors, ...row.warnings];
        const tag = <Tag color={color}>{label}</Tag>;
        return notes.length > 0 ? <Tooltip title={notes.join('; ')}>{tag}</Tooltip> : tag;
      },
    },
  ];

  return (
    <Modal
      title="Importă clienți din fișier"
      open={open}
      onCancel={handleClose}
      width={880}
      destroyOnHidden
      footer={
        phase === 'upload'
          ? [
              <Button key="cancel" onClick={handleClose}>
                Anulează
              </Button>,
            ]
          : [
              <Button key="back" disabled={phase === 'importing'} onClick={resetToUpload}>
                Înapoi
              </Button>,
              <Button key="cancel" disabled={phase === 'importing'} onClick={handleClose}>
                Anulează
              </Button>,
              <Button
                key="import"
                type="primary"
                disabled={newRows.length === 0}
                loading={commitMutation.isPending}
                onClick={() => commitMutation.mutate()}
              >
                Importă {newRows.length} client{newRows.length === 1 ? '' : 'i'}
              </Button>,
            ]
      }
    >
      {phase === 'upload' ? (
        <Spin spinning={previewMutation.isPending} tip="Se verifică statutul TVA la ANAF…">
          <Dragger
            accept=".xls,.xlsx,.csv"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file: RcFile) => {
              previewMutation.mutate(file);
              return false;
            }}
            style={{ padding: 24 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Trage fișierul aici sau apasă pentru a-l selecta</p>
            <p className="ant-upload-hint">.xls, .xlsx sau .csv — max 2MB</p>
          </Dragger>
          <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
            Coloane recunoscute: Denumire client (sau Nume/Client), CIF (sau CUI/Cod fiscal), Reg com, Adresa, Localitate,
            Judet, Banca, Iban, Email, Pers contact, Telefon.
          </Paragraph>
        </Spin>
      ) : (
        <Spin spinning={phase === 'importing'} tip="Se importă…">
          {preview && (
            <>
              <Alert
                style={{ marginBottom: 16 }}
                type={preview.summary.error > 0 ? 'warning' : 'info'}
                showIcon
                message={`${preview.summary.new} noi vor fi importați, ${preview.summary.duplicate} duplicate (se sar), ${preview.summary.error} cu erori.`}
              />
              <Table<ImportPreviewRow>
                rowKey="rowNumber"
                size="small"
                columns={columns}
                dataSource={preview.rows}
                pagination={false}
                scroll={{ y: 420 }}
              />
            </>
          )}
        </Spin>
      )}
    </Modal>
  );
}
